import type { DateTime } from "@/definitions/types";
import dayjs from "dayjs";
import { isValidDateFormat } from "@/utils/formatter";

export function isEmpty(val?: unknown | unknown[] | null) {
  if (Array.isArray(val)) {
    return val.length === 0;
  }
  return val === undefined || val === null || val === "";
}

export function isValidValue(value: unknown) {
  return value !== undefined && value !== null;
}

export function required(val: unknown | unknown[]): string | boolean {
  if (!isEmpty(val)) {
    return true;
  }
  return "입력 필수입니다.";
}

export const minLength =
  (min: number) =>
  (val: string | unknown[]): string | boolean => {
    if (isEmpty(val)) {
      return true;
    }
    if (Array.isArray(val)) {
      return min <= val.length || `${min}개(자리) 이상 등록 하세요!`;
    }
    return min <= val.length || `${min}개(자리) 이상 등록 하세요!`;
  };
export const maxLength =
  (max: number) =>
  (val: string): string | boolean => {
    if (isEmpty(val)) {
      return true;
    }
    if (Array.isArray(val)) {
      return val.length <= max || `${max}개(자리) 이하로 등록 가능합니다!`;
    }
    return val.length <= max || `${max}개(자리) 이하로 등록 가능합니다!`;
  };

/** 따옴표 없는 로컬부에 쓸 수 있는 ASCII 한 글자(RFC 5322 atext + 점). 비 ASCII 는 서버(SMTPUTF8)가 받으므로 따로 통과시킨다. */
const EMAIL_LOCAL_ASCII = /[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]/;
/** 도메인에 쓸 수 있는 ASCII 한 글자. 비 ASCII(국제화 도메인)는 서버가 IDNA 로 바꿔 받으므로 따로 통과시킨다. */
const EMAIL_DOMAIN_ASCII = /[A-Za-z0-9.-]/;

/** 모든 ASCII 글자가 허용 목록 안인가(비 ASCII 는 통과). 한 글자씩 보므로 입력 길이에 비례한다. */
function asciiCharsAllowed(text: string, allowed: RegExp): boolean {
  for (const char of text) {
    if (char.charCodeAt(0) < 128 && !allowed.test(char)) return false;
  }
  return true;
}

/** 점으로 나눈 조각이 모두 비지 않았는가 — 앞뒤 점 · 연속 점이 없다. */
function dotPartsFilled(text: string): boolean {
  return text.split(".").every((part) => part !== "");
}

/**
 * 서버(email-validator)가 전역 배달이 안 된다고 보고 거절하는 특수 용도 최상위 도메인(72차 CW72-03 — 입사서류 수신자 검사에서 이관).
 * 계정 이메일(`isEmail`) · 입사서류 수신자(`isValidRecipientEmail`)가 함께 쓴다.
 */
export const EMAIL_SPECIAL_USE_TLDS: ReadonlySet<string> = new Set([
  "arpa",
  "invalid",
  "local",
  "localhost",
  "onion",
  "test",
]);

/**
 * 계정 이메일(로그인 · 가입 · 구성원 초대) 형식 검사. 앞뒤 공백은 지운 값으로 본다 — 호출부도 지운 값을 보낸다.
 *
 * 서버(`EmailStr`)보다 **느슨해야 한다**(41차 I41-01). 계정을 만드는 서버 경로(가입 STEP 3 업로드 · 면접준비 「구성원으로 초대」)는
 * `o'brien@…` · 한글 로컬부 · 국제화 도메인을 받는데, 예전 ASCII 전용 정규식은 그 사람을 로그인 화면에서 막아 되찾을 길이 없었다.
 * 그래서 비 ASCII 와 `'` 같은 atext 는 받고, 서버가 **예외 없이** 거절하는 모양만 거른다(41차 fix1 A41-02 — 이걸 통과시키면 가입
 * 4단계 제출 전체가 어느 주소인지 모를 영문 422 로 실패했다): @ 하나 · 공백 없음, 로컬부의 ASCII 금지 문자(`,` `(` `)` 등) · 앞뒤 점 ·
 * 연속 점, 도메인의 ASCII 특수문자(영숫자 · `-` · `.` 밖, 예: `_`) · 앞뒤 점 · 연속 점 · 라벨 앞뒤 `-` · 숫자뿐인 최상위 도메인 ·
 * 점 없는 도메인, 특수 용도 최상위 도메인(`EMAIL_SPECIAL_USE_TLDS` — 72차 CW72-03, 예전에는 서버에 맡겨 칸 밖 영문 422 토스트가 났다).
 * 정규식은 한 글자 판정에만 쓴다(중첩 수량자는 `detect-unsafe-regex` 에 걸린다).
 */
export function isEmail(val: string): boolean | string {
  const trimmed = val.trim();
  if (isEmpty(trimmed)) {
    return true;
  }
  const at = trimmed.indexOf("@");
  const local = trimmed.slice(0, Math.max(at, 0));
  // 서버(IDNA)는 도메인의 마침표 변형(。．｡)을 ASCII 점으로 바꿔 받는다 — 판정만 바꾼 값으로 한다(41차 fix1 V41-07).
  const domain = trimmed.slice(at + 1).replace(/[。．｡]/g, ".");
  const labels = domain.split(".");
  const topLevel = labels[labels.length - 1] ?? "";
  const valid =
    !/\s/.test(trimmed) &&
    at > 0 &&
    at === trimmed.lastIndexOf("@") &&
    dotPartsFilled(local) &&
    asciiCharsAllowed(local, EMAIL_LOCAL_ASCII) &&
    labels.length > 1 &&
    dotPartsFilled(domain) &&
    asciiCharsAllowed(domain, EMAIL_DOMAIN_ASCII) &&
    labels.every((label) => !label.startsWith("-") && !label.endsWith("-")) &&
    !/^[0-9]+$/.test(topLevel);
  if (!valid) return "올바르지 않은 이메일 형식입니다!";
  // 서버(IDNA)는 전각 글자 · 대문자를 바꿔 받으므로 같은 모양으로 맞춰 본다.
  return (
    !EMAIL_SPECIAL_USE_TLDS.has(topLevel.normalize("NFKC").toLowerCase()) ||
    "사용할 수 없는 도메인입니다(예약 도메인)."
  );
}

export function isAlphanumeric(val: string): boolean | string {
  return (
    isEmpty(val) ||
    /^[a-zA-Z0-9]+$/.test(val) ||
    "영문, 숫자만 입력 가능합니다!"
  );
}
export function isDigits(val: string): boolean | string {
  return isEmpty(val) || /^[0-9]+$/.test(val) || "숫자만 입력 가능합니다!";
}
export function isNumeric(val: string): boolean | string {
  return (
    isEmpty(val) ||
    /^-?(\d+\.?\d*|\.\d+)$/.test(val) ||
    "숫자와 음수부호(-), 소수점(.) 입력만 가능합니다!"
  );
}

export const minDateValue =
  (minValue: DateTime) =>
  (val: string): string | boolean => {
    return (
      isEmpty(val) ||
      !isValidDateFormat(val) ||
      dayjs(val, "YYYY-MM-DD").isAfter(dayjs(minValue)) ||
      "날짜의 최소값이 잘못되었습니다!"
    );
  };
